/**
 * Admin Billing RPC definitions
 *
 * Admin endpoints for billing/subscription management — requires admin role.
 * Subscriptions are joined with customer (person), onboarding data, and node counts.
 * All data reads from DB (kept current by Stripe webhook handler).
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { SubscriptionPlan, SubscriptionStatus } from "@fm/db/frontend";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, RecordNotFoundError, Unauthorized } from "../errors.ts";

// ============================================================================
// Shared Schemas
// ============================================================================

/**
 * Customer data joined from the customers table.
 * @since 1.0.0
 */
export const BillingCustomer = Schema.Struct({
  customerId: Schema.String,
  name: Schema.String,
  email: Schema.String,
  stripeCustomerId: Schema.NullOr(Schema.String),
});
export type BillingCustomer = Schema.Schema.Type<typeof BillingCustomer>;

/**
 * Onboarding data joined from the onboardings table.
 * @since 1.0.0
 */
export const BillingOnboarding = Schema.Struct({
  onboardingId: Schema.String,
  name: Schema.String,
  email: Schema.String,
  phase: Schema.String,
  deploymentType: Schema.String,
});
export type BillingOnboarding = Schema.Schema.Type<typeof BillingOnboarding>;

/**
 * Node summary for billing context (agent installation).
 * @since 1.0.0
 */
export const BillingNode = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  status: Schema.String,
});
export type BillingNode = Schema.Schema.Type<typeof BillingNode>;

/**
 * Invoice event from webhook_events table.
 * @since 1.0.0
 */
export const BillingInvoiceEvent = Schema.Struct({
  id: Schema.String,
  externalId: Schema.String,
  eventType: Schema.String,
  processedAt: Schema.NullOr(Schema.String),
  createdAt: Schema.String,
});
export type BillingInvoiceEvent = Schema.Schema.Type<typeof BillingInvoiceEvent>;

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * Pagination + filter input for listing subscriptions
 * @since 1.0.0
 */
export const BillingListInput = Schema.Struct({
  limit: Schema.optionalWith(Schema.Number, { default: () => 50 }),
  offset: Schema.optionalWith(Schema.Number, { default: () => 0 }),
  status: Schema.optional(SubscriptionStatus),
});

/**
 * Input for finding a single subscription by ID
 * @since 1.0.0
 */
export const BillingFindByIdInput = Schema.Struct({
  id: Schema.String,
});

// ============================================================================
// Response Schemas
// ============================================================================

/**
 * Subscription summary for list view — includes customer, onboarding, and node count.
 * @since 1.0.0
 */
export const BillingSubscriptionSummary = Schema.Struct({
  id: Schema.String,
  customerId: Schema.String,
  plan: SubscriptionPlan,
  status: SubscriptionStatus,
  stripeSubscriptionId: Schema.NullOr(Schema.String),
  amount: Schema.NullOr(Schema.Number),
  currentPeriodStart: Schema.NullOr(Schema.DateTimeUtc),
  currentPeriodEnd: Schema.NullOr(Schema.DateTimeUtc),
  cancelAtPeriodEnd: Schema.Boolean,
  customer: Schema.NullOr(BillingCustomer),
  onboarding: Schema.NullOr(BillingOnboarding),
  nodeCount: Schema.Number,
  createdAt: Schema.DateTimeUtc,
  updatedAt: Schema.DateTimeUtc,
});
export type BillingSubscriptionSummary = Schema.Schema.Type<typeof BillingSubscriptionSummary>;

/**
 * Paginated response for billing subscriptions
 * @since 1.0.0
 */
export const BillingListResponse = Schema.Struct({
  items: Schema.Array(BillingSubscriptionSummary),
  total: Schema.Number,
});

/**
 * Full subscription detail — includes nodes and invoice history.
 * @since 1.0.0
 */
export const BillingSubscriptionDetail = Schema.Struct({
  id: Schema.String,
  customerId: Schema.String,
  plan: SubscriptionPlan,
  status: SubscriptionStatus,
  stripeSubscriptionId: Schema.NullOr(Schema.String),
  revenuecatAppUserId: Schema.NullOr(Schema.String),
  currentPeriodStart: Schema.NullOr(Schema.DateTimeUtc),
  currentPeriodEnd: Schema.NullOr(Schema.DateTimeUtc),
  cancelAtPeriodEnd: Schema.Boolean,
  customer: Schema.NullOr(BillingCustomer),
  onboarding: Schema.NullOr(BillingOnboarding),
  nodes: Schema.Array(BillingNode),
  invoiceHistory: Schema.Array(BillingInvoiceEvent),
  createdAt: Schema.DateTimeUtc,
  updatedAt: Schema.DateTimeUtc,
});
export type BillingSubscriptionDetail = Schema.Schema.Type<typeof BillingSubscriptionDetail>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Billing RPC group
 *
 * Provides subscription list and detail endpoints with customer, onboarding,
 * and node data. All endpoints require admin role via AdminMiddleware.
 *
 * @since 1.0.0
 */
export class AdminBillingRpcs extends RpcGroup.make(
  Rpc.make("admin.billing.findMany", {
    payload: BillingListInput,
    success: BillingListResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.billing.findById", {
    payload: BillingFindByIdInput,
    success: BillingSubscriptionDetail,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
) {}
